import {
   Flex,
   Link,
   Button,
   Menu,
   MenuButton,
   MenuList,
   MenuItem,
   LinkProps,
   MenuItemProps,
   ButtonProps,
   IconButton,
   useDisclosure,
   Tooltip,
} from '@chakra-ui/react';
import { FaIcon } from '@ifixit/icons';
import {
   faPenToSquare,
   faArrowsMaximize,
   faClockRotateLeft,
   faCircleStar,
   faDownload,
   faLanguage,
   faLinkSimple,
   faCode,
   faBellSlash,
   IconDefinition,
   faAngleDown,
   faMessagePlus,
   faBell,
   faMessageMinus,
   faCheck,
} from '@fortawesome/pro-regular-svg-icons';
import { faCircleStar as faFilledCircleStar } from '@fortawesome/pro-solid-svg-icons';
import { _js } from '@ifixit/localize';
import { ReactNode, useContext, MouseEventHandler } from 'react';
import { GuideSubscriptionContext, withGuideSubscription } from './GuideSubscription';
import { GuideFavoriteContext, withGuideFavorite } from './GuideFavorite';
import { GuideEmbedModal } from './GuideEmbedModal';
import { useExpandCollapseComments } from './GuideComments';
import { initializeChakraComponent } from 'Shared/chakra-initialize';
import copyToClipboard from 'Shared/copy_to_clipboard';
import { SharedNavBar, BreadcrumbData } from 'Guide/PageNavigation/SharedNavBar';

export interface NavData {
   breadCrumbs: Array<BreadcrumbData>;
   answersPartsUrl: string;
   devicePartsUrl?: string | null;
   fullScreenUrl: string;
   guidesUrl: string;
   sharableUrl: string;
   historyUrl?: string;
   pdfUrl?: string;
   translateUrl?: string;
   editUrl?: string;
   editTranslateUrl?: string;
   isTeardown: boolean;
   showExpandComments?: true;
}

export function GuideNav(props: NavData) {
   const { breadCrumbs, answersPartsUrl, devicePartsUrl, guidesUrl } = props;

   return (
      <SharedNavBar
         breadCrumbs={breadCrumbs}
         navTabs={[
            { name: _js('Parts'), url: devicePartsUrl ?? undefined },
            { name: _js('Guides'), url: guidesUrl, isCurrentPage: true },
            { name: _js('Answers'), url: answersPartsUrl },
         ]}
         editMenuComponent={<EditMenu {...props} />}
      />
   );
}

type StyledMenuItemProps = {
   as?: typeof Link | 'button';
   href?: string;
   icon?: IconDefinition;
   children?: ReactNode;
} & Omit<LinkProps | ButtonProps, 'link'> &
   Omit<MenuItemProps, 'icon'>;

function StyledMenuItem({ as, icon, children, ...menuItemProps }: StyledMenuItemProps) {
   // If link but no href, bail
   if (as == Link && !menuItemProps.href) {
      return null;
   }

   return (
      <MenuItem
         icon={
            icon ? (
               <FaIcon icon={icon} fontSize="16px" maxWidth="16px" width="16px" color="gray.600" />
            ) : undefined
         }
         as={as || 'button'}
         color="gray.900"
         _visited={{ color: menuItemProps.color || 'gray.900' }}
         _hover={{ textDecoration: 'none' }}
         fontSize="14px"
         {...menuItemProps}
      >
         {children}
      </MenuItem>
   );
}

function EditMenu({
   fullScreenUrl,
   sharableUrl,
   historyUrl,
   pdfUrl,
   translateUrl,
   editUrl,
   editTranslateUrl,
   showExpandComments,
   isTeardown,
}: NavData & { isTeardown: boolean }) {
   const guideSubscriptionContext = useContext(GuideSubscriptionContext);
   const guideFavoriteContext = useContext(GuideFavoriteContext);
   const { isOpen: isEmbedOpen, onOpen: onEmbedOpen, onClose: onEmbedClose } = useDisclosure();
   const [isExpanded, toggleExpandComments] = useExpandCollapseComments();

   return (
      <Flex>
         {(editUrl || editTranslateUrl) && (
            <Button
               as={Link}
               height="100%"
               color="brand.500"
               borderRadius="0px"
               borderColor="gray.200"
               border="none"
               borderLeft="1px solid"
               flexShrink={0}
               sx={{ '&': { borderColor: 'gray.200' } }}
               leftIcon={<FaIcon icon={faPenToSquare} fontSize="16px" color="brand.500" />}
               href={editUrl || editTranslateUrl}
               _visited={{ color: 'brand.500' }}
               _hover={{ textDecoration: 'none', background: 'gray.100' }}
               id="edit-nav-link"
            >
               {editUrl ? _js('Edit') : _js('Translate')}
            </Button>
         )}
         <Menu closeOnSelect={true} isLazy={true} lazyBehavior="keepMounted">
            <MenuButton
               as={IconButton}
               height="100%"
               color="brand.500"
               borderRadius="0px"
               border="none"
               borderColor="gray.200"
               borderLeft="1px solid"
               borderRight="1px solid"
               sx={{ '&': { borderColor: 'gray.200' } }}
               flexShrink={0}
               padding="0px 16px"
               icon={<FaIcon icon={faAngleDown} fontSize="16px" color="brand.500" />}
               className="guide-options-dropdown"
               aria-label={_js('Guide Options Menu')}
            />
            <MenuList zIndex={3} className="guide-options-dropdown-list">
               <StyledMenuItem
                  icon={faArrowsMaximize}
                  as={Link}
                  href={fullScreenUrl}
                  className="fullscreen-nav-link"
               >
                  {_js('Full screen')}
               </StyledMenuItem>
               <StyledMenuItem
                  icon={faClockRotateLeft}
                  as={Link}
                  href={historyUrl}
                  className="link-history"
               >
                  {_js('History')}
               </StyledMenuItem>
               {isTeardown && (
                  <StyledMenuItem
                     icon={guideFavoriteContext.isFavorite ? faFilledCircleStar : faCircleStar}
                     onClick={() => {
                        guideFavoriteContext.isFavorite
                           ? guideFavoriteContext.unfavorite(App.guideid, App.langid, isTeardown)
                           : guideFavoriteContext.favorite(App.guideid, App.langid, isTeardown);
                     }}
                     closeOnSelect={false}
                  >
                     {guideFavoriteContext.isFavorite
                        ? _js('Remove from Favorites')
                        : _js('Save to Favorites')}
                  </StyledMenuItem>
               )}
               <StyledMenuItem icon={faDownload} as={Link} href={pdfUrl}>
                  {_js('Download PDF')}
               </StyledMenuItem>
               <StyledMenuItem icon={faLanguage} as={Link} href={translateUrl}>
                  {_js('Translate')}
               </StyledMenuItem>
               {showExpandComments && (
                  <StyledMenuItem
                     icon={isExpanded ? faMessageMinus : faMessagePlus}
                     onClick={toggleExpandComments}
                     className="expand-comments"
                     closeOnSelect={false}
                  >
                     {isExpanded ? _js('Collapse Comments') : _js('Expand Comments')}
                  </StyledMenuItem>
               )}
               <CopyableStyledMenuItem
                  icon={faLinkSimple}
                  className="show-shareable-link"
                  closeOnSelect={false}
                  copyText={sharableUrl}
               >
                  {_js('Get shareable link')}
               </CopyableStyledMenuItem>
               <StyledMenuItem icon={faCode} onClick={onEmbedOpen} className="show-embed">
                  {_js('Embed this guide')}
                  <GuideEmbedModal
                     isOpen={isEmbedOpen}
                     onClose={onEmbedClose}
                     guideid={App.guideid}
                  />
               </StyledMenuItem>
               <StyledMenuItem
                  icon={guideSubscriptionContext.isSubscribed ? faBellSlash : faBell}
                  onClick={() => {
                     guideSubscriptionContext.isSubscribed
                        ? guideSubscriptionContext.unsubscribe(App.guideid, App.langid)
                        : guideSubscriptionContext.subscribe(App.guideid, App.langid);
                  }}
                  closeOnSelect={false}
               >
                  {guideSubscriptionContext.isSubscribed
                     ? _js('Stop Notifications')
                     : _js('Notify me on changes')}
               </StyledMenuItem>
            </MenuList>
         </Menu>
      </Flex>
   );
}

function CopyableStyledMenuItem({
   children,
   hideAfterMs = 1000,
   copyText,
   ...props
}: {
   hideAfterMs?: number;
   copyText: string;
   children: ReactNode;
} & StyledMenuItemProps) {
   const disclosureProps = useDisclosure(props);

   const onClick: MouseEventHandler<HTMLButtonElement> = () => {
      disclosureProps.onOpen();
      copyToClipboard(copyText);
      setTimeout(disclosureProps.onClose, hideAfterMs);
   };

   const icon = disclosureProps.isOpen ? faCheck : props.icon;

   return (
      <StyledMenuItem {...props} icon={icon} onClick={onClick}>
         <Tooltip hasArrow {...disclosureProps} label={_js('Copied!')}>
            {children}
         </Tooltip>
      </StyledMenuItem>
   );
}

initializeChakraComponent('GuideNavComponent', withGuideSubscription(withGuideFavorite(GuideNav)));
